import axios from "axios"
import moment from "moment"

import Auth from "../auth"
import Firebase from "../firebase"
import MQTT from "../mqtt"
import Sentry from "../sentry"
import Store from "../store"
import Actions from "../store/Actions"

const GET = "get"
const POST = "post"
const PATCH = "patch"
const DELETE = "delete"

function tokenExpired(creds) {
  const now = Math.floor(Date.now() / 1000)
  const issueDate = creds.dateIssued || 0
  return now - issueDate > creds.expiresIn
}

async function performRequest(method, endpoint, body = null) {
  try {
    const baseUrl = Firebase.getConfigValue("api_url")
    const apiKey = Firebase.getConfigValue("api_key")

    const url = baseUrl + endpoint
    let creds = Store.getState().credentials
    if (tokenExpired(creds)) {
      const newCreds = await Auth.refreshToken(creds)
      if (newCreds === null || typeof newCreds === "undefined") {
        console.log(
          "TOKEN EXPIRED AND REFRESH TOKEN NULL, LOGGING OUT: ",
          creds,
        )
        Sentry.addBreadcrumb({ data: creds, message: "Previous credentials" })
        Sentry.logMessage("Unable to refresh Auth0 token, logging user out.")
        logout()
        return null
      }
      Store.dispatch(Actions.setCredentials(newCreds))
      creds = newCreds
    }

    var params = {
      method,
      url,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + creds.accessToken,
        "x-api-key": apiKey,
      },
    }
    if (body !== null) {
      params.data = JSON.stringify(body)
    }

    return axios(params)
      .then(response => {
        if (response) {
          return response.data
        }
        return null
      })
      .catch(error => {
        Sentry.logError(error)
        console.log("axios.error", { ...error })
        return null
      })
  } catch (error) {}
}

async function getBusiness() {
  return await performRequest(GET, "/admin/business")
}

async function getAllVenues() {
  return await performRequest(GET, "/admin/business/venues")
}

async function getVenue(venue) {
  return await performRequest(GET, `/admin/business/venues/${venue._id}`)
}

async function updateTimeSlots(disabledTimeslots, service) {
  const venue = Store.getState().venue
  const status = { [service.key]: { disabledTimeslots } }
  const response = await performRequest(
    PATCH,
    `/admin/business/venues/${venue._id}`,
    status,
  )
  return response && response._id
}

async function updateSlotInterval(value, service) {
  const venue = Store.getState().venue
  const status = {
    [service.key]: {
      deliveryTimeslots: {
        ...service.deliveryTimeslots,
        slotInterval: value,
      },
    },
  }
  const response = await performRequest(
    PATCH,
    `/admin/business/venues/${venue._id}`,
    status,
  )
  console.log(response)
  return response && response._id
}

async function updatePrepTime(value, service) {
  const venue = Store.getState().venue
  const status = {
    [service.key]: {
      deliveryTimeslots: {
        ...service.deliveryTimeslots,
        prepTime: value,
      },
    },
  }
  const response = await performRequest(
    PATCH,
    `/admin/business/venues/${venue._id}`,
    status,
  )
  return response && response._id
}

async function setServiceStatus(venue, service) {
  try {
    const status = { [service.key]: { isEnabled: service.isEnabled } }
    const response = await performRequest(
      PATCH,
      `/admin/business/venues/${venue._id}`,
      status,
    )
    return response && response._id
  } catch (error) {
    console.log(error)
    return null
  }
}

async function registerForPushMessages() {
  const token = await Firebase.registerForPush()
  const venue = Store.getState().venue
  const body = { token }
  const response = await performRequest(
    POST,
    `/admin/business/venues/${venue._id}/push-tokens`,
    body,
  )
  return !response
}

async function removePushToken(token) {
  const venue = Store.getState().venue
  await Firebase.deletePushToken()
  if (!venue || !token) {
    return null
  }
  const body = { token }
  const response = await performRequest(
    DELETE,
    `/admin/business/venues/${venue._id}/push-tokens`,
    body,
  )
  return response != null
}

async function getAllOrders() {
  const venue = Store.getState().venue
  const response =
    (await performRequest(GET, `/admin/business/venues/${venue._id}/orders`)) ||
    []
  const orders = response.sort(
    (a, b) => moment(a.timeslot) > moment(b.timeslot),
  )
  return orders
}

async function getOrder(orderId) {
  const venue = Store.getState().venue
  const order = await performRequest(
    GET,
    `/admin/business/venues/${venue._id}/orders/${orderId}`,
  )
  return order
}

async function updateOrderStatus(order, status) {
  const venue = Store.getState().venue
  const body = { status }
  const response = await performRequest(
    POST,
    `/admin/business/venues/${venue._id}/orders/${order._id}/status`,
    body,
  )
  console.log("RESPONSE: ", JSON.stringify(response))
  return response
}

async function getMenu() {
  const venue = Store.getState().venue
  return await performRequest(GET, `/admin/business/venues/${venue._id}/menu`)
}

function buildFlooidMenuUpdateUrl(menuId, item) {
  let url = `/admin/menus/flooid/${menuId}`
  if (item) {
    switch (item.__type) {
      case "MenuCategory": {
        url += `/categories/${item._id}`
        break
      }
      case "MenuItem": {
        url += `/items/${item._id}`
        break
      }
      case "MenuChoiceGroup": {
        url += `/choice-groups/${item._id}`
        break
      }
      case "MenuChoiceGroupItem": {
        url += `/choice-groups/${item.groupId}/choices/${item._id}`
        break
      }
    }
  }

  return url
}

async function updateFlooidMenuStock(item, inStock) {
  try {
    const service = Store.getState().service
    const menuId = service?.menu?._id
    if (!menuId || !item) {
      return null
    }

    const url = buildFlooidMenuUpdateUrl(menuId, item)
    const body = { inStock }
    const response = await performRequest(PATCH, url, body)

    return response != null && response._id != null
  } catch (error) {
    console.log(error)
    return null
  }
}

async function updateMenuStock(item, inStock) {
  try {
    const venue = Store.getState().venue
    const service = Store.getState().service

    const isFlooid = service?.menu?.provider === "Flooid"

    if (isFlooid) {
      return await updateFlooidMenuStock(item, inStock)
    }

    let url = `/admin/business/venues/${venue._id}/menus/${service.key}`
    if (item.__type === "MenuItem") {
      url = url + `/items/${item._id}`
    } else {
      url =
        url +
        `/items/${item.itemId}/choice-groups/${item.groupId}/choices/${item._id}`
    }
    const body = { inStock }
    const response = await performRequest(PATCH, url, body)
    return response != null && response._id != null
  } catch (error) {
    console.log(error)
    return null
  }
}

async function updateFlooidMenuStatus(item, status) {
  try {
    const service = Store.getState().service
    const menuId = service?.menu?._id
    if (!menuId || !item) {
      return false
    }
    let url = buildFlooidMenuUpdateUrl(menuId, item)
    const key = item.__type === "MenuItem" ? "active" : "enabled"
    const body = { [key]: status }

    const response = await performRequest(PATCH, url, body)

    return response != null && response._id != null
  } catch (error) {
    console.log(error)
    return false
  }
}

// This definitely needs refactoring.. urgh
async function updateMenuStatus(item, status) {
  try {
    const venue = Store.getState().venue
    const service = Store.getState().service

    const isFlooid = service?.menu?.provider === "Flooid"

    if (isFlooid) {
      return await updateFlooidMenuStatus(item, status)
    }

    let url = `/admin/business/venues/${venue._id}/menus/${service.key}`
    if (item.parentCatId || item.categoryId) {
      url = url + `/cats/${item.parentCatId ?? item.categoryId}`
      if (item.parentCatId) {
        url = url + `/cats/${item.categoryId}`
      }
    } else if (item.__type === "MenuChoiceGroup" || item.type === "Choice") {
      url = url + `/items/${item.itemId}/choice-groups/${item.groupId}`
      if (item.type === "Choice") {
        url = url + `/choices/${item._id}`
      }
    } else {
      url = url + `/items/${item._id}`
    }

    console.log("TOGGLE URL: ", url)

    const key = item.__type === "MenuItem" ? "isActive" : "enabled"
    const body = { [key]: status }
    const response = await performRequest(PATCH, url, body)
    return response != null && response._id != null
  } catch (error) {
    return false
  }
}

async function logout() {
  try {
    let token = await Firebase.getPushToken()
    await removePushToken(token)
    Store.dispatch(Actions.logout())
    MQTT.disconnect()
    return true
  } catch (error) {
    console.log(error)
    Store.dispatch(Actions.logout())
    return false
  }
}

export default {
  getBusiness,
  getAllVenues,
  getVenue,
  setServiceStatus,
  updateTimeSlots,
  getAllOrders,
  getOrder,
  updateOrderStatus,
  getMenu,
  updateMenuStock,
  updateMenuStatus,
  updateSlotInterval,
  updatePrepTime,
  registerForPushMessages,
  removePushToken,
  logout,
}
